import { Alert, Button, Checkbox, Field, Modal } from '@catering/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { Save } from 'lucide-react';
import { type JSX, useEffect } from 'react';
import { type Resolver, useForm, useWatch } from 'react-hook-form';

import { OperatingUnitSelect } from '../../../components/operating-unit-select';
import { listSites, searchAllCustomers } from '../../../customers/api';
import { listMenuPositions, listServiceTypes } from '../../../menus/api';
import { DAYS_OF_WEEK, DAY_OF_WEEK_LABELS } from '../../../orders/api';
import { mapOrderError } from '../../../orders/order-api-errors';
import {
  orderTemplateFormSchema,
  type OrderTemplateFormValues,
} from '../../../orders/order-template-form-schema';
import { useCustomerUnitIds } from '../../../orders/use-customer-unit-ids';
import { MenuLineEditor } from './menu-line-editor';

export interface OrderTemplateFormModalProps {
  mode: 'create' | 'edit';
  defaultValues: OrderTemplateFormValues;
  submitting: boolean;
  error: unknown;
  onSubmit: (values: OrderTemplateFormValues) => void | Promise<void>;
  onClose: () => void;
}

/**
 * `order-templates/spec.md` → "ABM de plantillas por cliente y sede".
 * `FormSection` per block (cliente+sede, días de la semana, líneas),
 * same react-hook-form + zodResolver shape as `OrderForm` (PR 5).
 */
export function OrderTemplateFormModal({
  mode,
  defaultValues,
  submitting,
  error,
  onSubmit,
  onClose,
}: OrderTemplateFormModalProps): JSX.Element {
  const {
    register,
    handleSubmit,
    control,
    setError,
    setValue,
    formState: { errors },
  } = useForm<OrderTemplateFormValues, unknown, OrderTemplateFormValues>({
    resolver: zodResolver(orderTemplateFormSchema) as unknown as Resolver<
      OrderTemplateFormValues,
      unknown,
      OrderTemplateFormValues
    >,
    defaultValues,
  });

  const customerId = useWatch({ control, name: 'customerId' });
  const operatingUnitId = useWatch({ control, name: 'operatingUnitId' });

  // The point of a template is fixed on creation (`updateOrderTemplateSchema` has no such field).
  const customerUnitIds = useCustomerUnitIds(mode === 'create' ? customerId : '');
  const needsOperatingUnit = customerUnitIds.length > 1;

  useEffect(() => {
    if (mode === 'create') {
      setValue('operatingUnitId', '');
    }
  }, [mode, customerId, setValue]);

  function handleValidSubmit(values: OrderTemplateFormValues): void | Promise<void> {
    if (needsOperatingUnit && values.operatingUnitId === '') {
      setError('operatingUnitId', { type: 'required', message: 'Seleccioná un punto de venta' });
      return;
    }

    return onSubmit(needsOperatingUnit ? values : { ...values, operatingUnitId: '' });
  }

  const customersQuery = useQuery({
    queryKey: ['customers-for-order-templates'],
    queryFn: () => searchAllCustomers({ q: undefined, zone: undefined, status: undefined }),
  });

  const sitesQuery = useQuery({
    queryKey: ['sites-for-order-templates', customerId],
    queryFn: () => listSites(customerId),
    enabled: customerId !== '',
  });

  const positionsQuery = useQuery({ queryKey: ['menu-positions'], queryFn: listMenuPositions });
  const serviceTypesQuery = useQuery({ queryKey: ['service-types'], queryFn: listServiceTypes });

  const serviceTypeNames = Object.fromEntries(
    (serviceTypesQuery.data ?? []).map((serviceType) => [serviceType.id, serviceType.name]),
  );
  const activePositions =
    positionsQuery.data === undefined
      ? undefined
      : positionsQuery.data
          .filter((position) => position.isActive)
          .map((position) => ({
            positionId: position.id,
            positionName: position.name,
            serviceTypeId: position.serviceTypeId,
            serviceTypeName: serviceTypeNames[position.serviceTypeId] ?? '—',
            displayOrder: position.displayOrder,
          }));
  const errorMessage = mapOrderError(error);

  return (
    <Modal
      open
      onClose={onClose}
      title={mode === 'create' ? 'Nueva plantilla' : 'Editar plantilla'}
      className="max-w-2xl"
    >
      <form
        onSubmit={(event) => void handleSubmit(handleValidSubmit)(event)}
        className="flex flex-col gap-6"
        noValidate
      >
        {errorMessage !== '' ? <Alert tone="danger">{errorMessage}</Alert> : null}

        <Field
          label="Nombre"
          required
          {...register('name')}
          {...maybeError(errors.name?.message)}
        />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="template-customer" className="text-sm font-medium text-slate-900">
              Empresa
              <span aria-hidden="true" className="ml-0.5 text-red-600">
                *
              </span>
            </label>
            <select
              id="template-customer"
              className="h-11 rounded-md border border-slate-300 px-3 text-sm"
              {...register('customerId')}
            >
              <option value="">Seleccioná una empresa</option>
              {(customersQuery.data?.items ?? []).map((customer) => (
                <option key={customer.id} value={customer.id}>
                  {customer.legalName}
                </option>
              ))}
            </select>
            {errors.customerId?.message === undefined ? null : (
              <p role="alert" className="text-xs text-red-600">
                {errors.customerId.message}
              </p>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="template-site" className="text-sm font-medium text-slate-900">
              Sede
              <span aria-hidden="true" className="ml-0.5 text-red-600">
                *
              </span>
            </label>
            <select
              id="template-site"
              disabled={customerId === ''}
              className="h-11 rounded-md border border-slate-300 px-3 text-sm disabled:bg-slate-50"
              {...register('siteId')}
            >
              <option value="">Seleccioná una sede</option>
              {(sitesQuery.data ?? []).map((site) => (
                <option key={site.id} value={site.id}>
                  {site.name}
                </option>
              ))}
            </select>
            {errors.siteId?.message === undefined ? null : (
              <p role="alert" className="text-xs text-red-600">
                {errors.siteId.message}
              </p>
            )}
          </div>
        </div>

        {needsOperatingUnit ? (
          <OperatingUnitSelect
            id="template-operating-unit"
            label="Punto de venta"
            required
            restrictTo={customerUnitIds}
            value={operatingUnitId}
            onChange={(value) => setValue('operatingUnitId', value, { shouldValidate: true })}
            error={errors.operatingUnitId?.message}
          />
        ) : null}

        <fieldset className="flex flex-col gap-2">
          <legend className="text-sm font-medium text-slate-900">Días de la semana</legend>
          <div className="flex flex-wrap gap-3">
            {DAYS_OF_WEEK.map((day) => (
              <Checkbox
                key={day}
                label={DAY_OF_WEEK_LABELS[day]}
                value={day}
                {...register('daysOfWeek')}
              />
            ))}
          </div>
          {errors.daysOfWeek?.message === undefined ? null : (
            <p role="alert" className="text-xs text-red-600">
              {errors.daysOfWeek.message}
            </p>
          )}
        </fieldset>

        <div className="flex flex-col gap-3">
          <h2 className="text-sm font-semibold text-slate-900">Líneas de la plantilla</h2>
          <MenuLineEditor
            control={control}
            register={register}
            positions={activePositions}
            errors={errors.items}
          />
        </div>

        {mode === 'edit' ? <Checkbox label="Plantilla activa" {...register('isActive')} /> : null}

        <div className="flex items-center justify-end gap-3">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" icon={Save} loading={submitting}>
            Guardar
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function maybeError(message: string | undefined): { error: string } | Record<string, never> {
  return message === undefined ? {} : { error: message };
}
